import * as Turbo from "@hotwired/turbo"

// Builds a flat /exhibitor/leads drilldown URL and navigates to it.
// `filters` is a plain object of friendly param keys. Each key carries a
// SINGLE scalar value (e.g. quality_band: "buyer"); the server wraps that
// scalar into a one-element array (LeadFilters.coerce_scalar_array_params)
// before the array-only permit. NEVER pre-join multiple values on commas:
// a comma-bearing string is treated as ONE literal value — which is correct
// for real names like "Bonaire, Sint Eustatius and Saba", but means a fake
// CSV like "NY,CA" matches nothing. For a genuinely multi-value filter, pass
// repeated keys / an array shape instead of a joined string.
// Turbo.visit performs a page-level visit — it ignores any enclosing
// turbo-frame, which is what we want (the drill leaves the Reports Hub).
// Keep the param keys in sync with DrilldownsHelper#lead_drill_url:
// the LeadFilters::CHIP_LABELS keys plus date_preset, from_date,
// to_date, target_event_id, segment_origin, segment_anchor, q.
// LRPO-5208 / LRPO-5317 / LRPO-5318 / LRPO-5514 — a drill must carry the ACTIVE
// Reports date window so it reconciles with the bar/card/cell it came from.
//
// ---------------------------------------------------------------------------
// THIS FILE CONTAINS NO DATE LOGIC. THAT IS DELIBERATE. DO NOT ADD ANY.
// ---------------------------------------------------------------------------
// It used to. It re-derived the window from window.location.search: re-parse the
// bounds, re-check they were real calendar dates, re-check the ordering, re-check
// membership of the event window. That was a SECOND implementation of the server's
// Reports::DateWindow, written in JavaScript — and the two drifted apart again and
// again. Every drift had the same shape and the same cost: a range one half called
// unusable and the other called fine, so a KPI card rendered 0 while the chart next
// to it drilled to EVERY LEAD THE EXHIBITOR EVER SCANNED (LeadFilters FAILS OPEN on
// any custom range it cannot use — `return scope unless range`, lead_filters.rb
// :352-354 — so the scope comes back COMPLETELY UNFILTERED).
//
// Six review rounds found six variants of that one bug: a missing bound, an
// inverted range, a non-ISO bound, an impossible calendar date (2026-02-30 — which
// `new Date()` does not even reject, it silently ROLLS OVER to 2026-03-02), a range
// disjoint from the event window. Patching them one at a time never converged,
// because the bug was never any single one of them: it was that the window was
// being resolved in two places at all.
//
// So it is resolved in ONE place — Reports::DateWindow, on the server — and the
// server hands us the ANSWER. ReportsHelper#reports_tab_frame stamps
// Reports::DateWindow#drill_params (the very same hash the server-rendered KPI card
// hrefs are built from) into the tab frame as data-report-drill-window. We read it
// and pass it through. There is nothing left here to get out of sync.
//
// WHY NOT THE URL? Because the URL is not the answer — it is the QUESTION. It holds
// the bounds the USER TYPED, and it must: the date pickers have to keep showing the
// user their mistake so they can fix it. For an unusable range those bounds are NOT
// the window the charts were computed from, and turning one into the other is
// exactly the derivation that kept drifting.
//
// WHY NOT THE FILTER BAR? `_filter_bar.html.erb` renders OUTSIDE the
// reports_tab_content frame and is never re-rendered by a frame swap, so a window
// stamped there would go STALE the moment a filter is applied in-page — describing
// the previous render while the charts on screen came from the new one. Turbo
// replaces a frame's CHILDREN, so a stamp INSIDE the frame is always exactly as
// fresh as the charts beside it. (data-full-event-from/-to are safe on the bar only
// because they describe the EVENT, not the filter.)

// Ordered, ISO, and old enough that no lead can fall in it — so LeadFilters APPLIES
// it and returns zero rows instead of DROPPING it and returning every lead. Mirrors
// Reports::DateWindow::UNSATISFIABLE_DRILL_DATE.
//
// This is the ONLY date constant left in this file, and it is not a derivation: it
// is the fail-closed default for when we cannot read the server's answer at all.
const UNSATISFIABLE_DRILL_DATE = "1970-01-01"

function unsatisfiableCustomWindow() {
  return {
    date_preset: "custom",
    from_date: UNSATISFIABLE_DRILL_DATE,
    to_date: UNSATISFIABLE_DRILL_DATE
  }
}

function activeReportDateParams() {
  // Gate on the REPORTS filter bar — it is the only thing that establishes an
  // active Reports window. Off the Reports Hub (the Dashboard) there is no window
  // and a drill is correctly unwindowed, exactly as before.
  //
  // Deliberately NOT `input[name="date_preset"]`: the LEADS index renders its own
  // hidden `date_preset` input (leads/filter_sections/_when.html.erb), and matching
  // that would make a drill re-inject a window on a page that is not the Reports Hub.
  const bar = document.querySelector('[data-controller~="reports-filter"]')
  if (!bar) return {}

  const stamp = document.querySelector("[data-report-drill-window]")

  // ON the Reports Hub with NO server answer to read. Do NOT fall back to an
  // unwindowed drill — that is the LRPO-5317/5318/5321 over-count (the drilled list
  // applies no window of its own, so omitting one lists every lead ever scanned).
  // FAIL CLOSED: an unusable filter may only ever NARROW, never widen.
  if (!stamp) return unsatisfiableCustomWindow()

  let parsed
  try {
    parsed = JSON.parse(stamp.getAttribute("data-report-drill-window"))
  } catch (_e) {
    return unsatisfiableCustomWindow()
  }

  // A plain object is the contract. `{}` is a REAL answer (the draft-event
  // full_event case: no resolvable window, drill unwindowed), and is distinct from
  // a missing or malformed stamp, which is not an answer at all.
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    return unsatisfiableCustomWindow()
  }

  return parsed
}

// LRPO-5201 — the Lead Quality tab's active "Filter by Scanner" narrowing, read
// from the stamp ReportsHelper#report_drill_scanner_stamp renders INSIDE that tab
// (`{"scanner_id": "<user id>"}`). Same server-hands-us-the-answer contract as the
// date window above: the JS derives nothing.
//
// WHY EVERY CHART DRILL ON THAT TAB NEEDS IT. Once LRPO-5201 made the tab's charts
// read the per-activation scope for the selected scanner, a drill that omitted the
// scanner would land on the event-wide list — chart says 2, list shows thousands.
// Merging it HERE fixes all four JS drills on the tab at once (score distribution,
// quality trend, company-type, and the high-value-by-hour heatmap, which reaches us
// through chart_controller's data-drill), so none can be forgotten individually.
//
// SCOPED BY THE DOM, NOT BY A FLAG: the stamp is rendered only by the Lead Quality
// partial, so it is present only while that tab is on screen. Other Reports tabs
// (and the /exhibitor/leads index, which also calls leadDrill from _table) find no
// stamp and are unaffected — their charts apply no scanner narrowing either.
//
// ABSENT = "All scanners" = no narrowing, which is the correct default. Unlike the
// date window there is nothing to fail closed about: a missing scanner stamp yields
// the same event-wide drill the unfiltered tab is already showing.
function activeReportScannerParams() {
  const stamp = document.querySelector("[data-report-drill-scanner]")
  if (!stamp) return {}

  try {
    const parsed = JSON.parse(stamp.getAttribute("data-report-drill-scanner"))
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {}
    return parsed
  } catch (_e) {
    return {}
  }
}

// Present only on Compare when the user picked Today, Yesterday, or Custom.
// Missing means Full Event: leave the past show alone. A broken stamp
// fails closed so the list cannot grow to every lead.
function activeCompareDateParams() {
  const stamp = document.querySelector("[data-report-compare-date]")
  if (!stamp) return {}

  let parsed
  try {
    parsed = JSON.parse(stamp.getAttribute("data-report-compare-date"))
  } catch (_e) {
    return unsatisfiableCustomWindow()
  }
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    return unsatisfiableCustomWindow()
  }
  return parsed
}

export function leadDrill(filters, { origin = null, anchor = null, targetEventId = null } = {}) {
  const params = new URLSearchParams()
  // Same-event drills use the Reports stamp. Cross-event drills use a
  // separate Compare stamp that exists only for Today, Yesterday, and
  // Custom. Full Event has no stamp, so a past show stays on its own dates.
  // Explicit keys in `filters` still win.
  const activeWindow = targetEventId ? activeCompareDateParams() : activeReportDateParams()
  // LRPO-5201 — likewise inherit the Lead Quality tab's Scanner narrowing (a no-op
  // hash everywhere else). Placed BEFORE `filters` in the spread so an explicit
  // scanner_id in the drill still WINS: the "Average Quality by Scanner" bar IS the
  // scanner dimension and emits its own bar's scanner_id, which must not be
  // overridden by the tab filter. (When a scanner is selected the chart renders only
  // that one bar, so the two agree anyway — but the precedence is the point.)
  const activeScanner = targetEventId ? {} : activeReportScannerParams()
  const merged = { ...activeWindow, ...activeScanner, ...(filters || {}) }
  Object.entries(merged).forEach(([k, v]) => {
    if (v === null || v === undefined) return
    // Array-shaped filter value (e.g. scan_hour: [8, 9] from the Hourly Heatmap
    // bars drill). Emit repeated `key[]` params so Rails' array permit
    // (LeadFilters PERMITTED_PARAM_SHAPE, e.g. `{ scan_hour: [] }` +
    // integer_array(:scan_hour)) receives a real multi-value Array. The scalar
    // branch below is UNCHANGED — only Arrays take this path, so the matrix
    // drills and every other scalar caller serialize byte-identically. Never
    // `String([8, 9])` here: that joins on commas ("8,9") → one token the
    // server reads as a single hour and matches nothing.
    if (Array.isArray(v)) {
      v.forEach((el) => {
        if (el !== null && el !== undefined && String(el).length) params.append(`${k}[]`, String(el))
      })
      return
    }
    if (String(v).length) params.set(k, String(v))
  })
  if (targetEventId) params.set("target_event_id", targetEventId)
  if (origin) params.set("segment_origin", origin)
  if (anchor) params.set("segment_anchor", anchor)
  // Announce the in-flight navigation to assistive tech. Turbo resets body
  // state on the new page load, so no manual cleanup is needed.
  document.body.setAttribute("aria-busy", "true")
  Turbo.visit(`/exhibitor/leads?${params.toString()}`)
}

// Chartkick charts are created by per-chart inline <script> blocks emitted
// by the Ruby `area_chart`/`bar_chart` helpers — those serialize options to
// JSON, so a Chart.js `onClick` *function* cannot be passed through the Ruby
// `library:` hash. The drilldown for a Chartkick chart is therefore wired by
// a sibling inline <script> in the partial that patches the chart's Chart.js
// `onClick` after creation. Inline scripts cannot `import`, so `leadDrill` is
// mirrored onto `window` here for those scripts to reach. (ES-module callers —
// chart_controller, geo_chart_controller, multi_heatmap_controller — keep
// using the named export.)
if (typeof window !== "undefined") {
  window.leadDrill = leadDrill
}
